import { Service } from '@genial/services';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';

import { removeAuthorizationHeader } from '../../pages/BatchTicketInvestLink/Constants';

import {
  blockListConfirmTokenState,
  blockListUnlockCustomerSendState,
  blockListRetryUnlockCustomerSendState,
} from './atom';

export const useBlockListActions = () => {
  const setBlockListConfirmTokenState = useSetRecoilState(blockListConfirmTokenState);

  const sendToken = useCallback(
    async (confirmationToken: string) => {
      try {
        const params = {
          screenName: 'unlock-confirmation',
          optinConfirmTokenRequest: {
            token: confirmationToken,
          },
        };

        await Service.Customer.V1.postOptinConfirmToken(params, removeAuthorizationHeader());

        setBlockListConfirmTokenState({
          isConfirmed: true,
          isExpired: false,
          isLoading: false,
        });
      } catch (_error) {
        setBlockListConfirmTokenState({
          isConfirmed: false,
          isExpired: true,
          isLoading: false,
        });
      }
    },
    [setBlockListConfirmTokenState]
  );

  const setSendUnlockCustomerState = useSetRecoilState(blockListUnlockCustomerSendState);

  const sendUnlock = useCallback(
    ({
      customerId,
      channel,
      observation,
      optoutId,
    }: {
      customerId: number;
      channel: string;
      observation: string;
      optoutId: string;
    }) => {
      const handleError = () => {
        setSendUnlockCustomerState({
          isSend: false,
          isError: true,
          isLoading: false,
        });
      };

      setSendUnlockCustomerState({
        isSend: false,
        isError: false,
        isLoading: true,
      });

      Service.Customer.V1.postOptinSend({
        optinSendEmailRequest: {
          customerId,
          channel,
          observation,
          optoutId,
        },
        screenName: 'unlock-customer',
      })
        .then(() => {
          setSendUnlockCustomerState({
            isSend: true,
            isError: false,
            isLoading: false,
          });
        })
        .then(() => {
          // Reset state after successful request
          setSendUnlockCustomerState({
            isSend: false,
            isError: false,
            isLoading: false,
          });
        })
        .catch(handleError)
        .then(() => {
          // Reset state after error
          setSendUnlockCustomerState({
            isSend: false,
            isError: false,
            isLoading: false,
          });
        });
    },
    [setSendUnlockCustomerState]
  );

  const setSendRetryUnlockCustomerState = useSetRecoilState(blockListRetryUnlockCustomerSendState);

  const retryUnlock = useCallback(
    ({ customerId, channel }) => {
      const handleError = () => {
        setSendRetryUnlockCustomerState({
          isSend: false,
          isError: true,
          isLoading: false,
        });
      };

      setSendRetryUnlockCustomerState({
        isSend: false,
        isError: false,
        isLoading: true,
      });

      Service.Customer.V1.postOptinRetry({
        optinRetryRequest: {
          customerId,
          channel,
        },
        screenName: 'unlock-customer',
      })
        .then(() => {
          setSendRetryUnlockCustomerState({
            isSend: true,
            isError: false,
            isLoading: false,
          });
        })
        .then(() => {
          // Reset state after successful request
          setSendRetryUnlockCustomerState({
            isSend: false,
            isError: false,
            isLoading: false,
          });
        })
        .catch(handleError)
        .then(() => {
          // Reset state after error
          setSendRetryUnlockCustomerState({
            isSend: false,
            isError: false,
            isLoading: false,
          });
        });
    },
    [setSendRetryUnlockCustomerState]
  );

  return {
    sendToken,
    sendUnlock,
    retryUnlock,
  };
};
